import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Mail, Smartphone } from "lucide-react";
import { WalletArt } from "@/components/ww/WalletArt";
import { useAuth } from "@/lib/auth";
import { Spinner } from "@/components/ww/Misc";
import { PhoneAuth } from "@/components/auth/PhoneAuth";
import { EmailAuth, GoogleButton } from "@/components/auth/EmailAuth";

const Tab = ({ active, onClick, icon: I, label, testid }) => (
  <button type="button" onClick={onClick} className={`flex flex-1 items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-semibold transition-colors ${active ? "bg-white text-ww-navy shadow-sm" : "text-ww-muted hover:text-ww-navy"}`} data-testid={testid}><I className="h-4 w-4" />{label}</button>
);

export default function AuthPage({ mode }) {
  const { isLoaded, isSignedIn } = useAuth();
  const [tab, setTab] = useState("mobile");
  const [err, setErr] = useState("");
  if (!isLoaded) return <Spinner />;
  if (isSignedIn) return <Navigate to="/wallet" replace />;
  const signUp = mode === "sign-up";
  return (
    <main className="grid min-h-[calc(100vh-72px)] lg:grid-cols-2" data-testid={`auth-page-${mode}`}>
      <div className="relative hidden flex-col items-center justify-center overflow-hidden bg-[linear-gradient(150deg,#EAF9F4,#EAF9F9)] p-12 lg:flex">
        <div className="w-full max-w-md"><WalletArt compact /></div>
        <h2 className="mt-6 text-center font-display text-2xl font-semibold text-ww-navy">Every Purchase. Every Promise. One Wallet.</h2>
        <p className="mt-2 text-center text-sm text-ww-muted">A simple, secure and quick sign up.</p>
      </div>
      <div className="flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-[28px] border border-ww-border bg-white p-6 shadow-[0_30px_80px_-40px_rgba(16,43,64,0.35)] sm:p-8" data-testid="auth-card">
          <h1 className="font-display text-3xl font-semibold text-ww-navy">{signUp ? "Create Your Free Wallet" : "Welcome Back"}</h1>
          <p className="mt-2 text-sm text-ww-muted">{signUp ? "Sign up with your mobile number, email or Google." : "Sign in to your Warranty Wallet."}</p>
          <div className="mt-6"><GoogleButton onError={setErr} /></div>
          <div className="my-5 flex items-center gap-3 text-xs font-medium uppercase tracking-wider text-ww-muted"><span className="h-px flex-1 bg-ww-border" />or<span className="h-px flex-1 bg-ww-border" /></div>
          <div className="mb-5 flex gap-1 rounded-2xl bg-ww-cyan/60 p-1">
            <Tab active={tab === "mobile"} onClick={() => setTab("mobile")} icon={Smartphone} label="Mobile" testid="auth-tab-mobile" />
            <Tab active={tab === "email"} onClick={() => setTab("email")} icon={Mail} label="Email" testid="auth-tab-email" />
          </div>
          {tab === "mobile" ? <PhoneAuth /> : <EmailAuth key={mode} mode={mode} />}
          {err && <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700" role="alert" data-testid="auth-error">{err}</p>}
          <p className="mt-6 text-center text-sm text-ww-muted">{signUp ? "Already have a wallet? " : "New to Warranty Wallet? "}
            <Link to={signUp ? "/sign-in" : "/sign-up"} className="font-semibold text-ww-emerald hover:underline" data-testid="auth-switch-mode-link">{signUp ? "Sign in" : "Create a free account"}</Link></p>
          <p className="mt-4 text-center text-[11px] leading-relaxed text-ww-muted">By continuing you agree to our <Link to="/terms" className="underline">Terms</Link> and <Link to="/privacy" className="underline">Privacy Policy</Link>. Protected by reCAPTCHA.</p>
        </div>
      </div>
    </main>
  );
}
